import { useEffect } from 'react';
import { useStore } from '../store/useStore';
import { useDocControllerContext } from '../hooks/DocControllerContext';
import { useT } from '../i18n/useT';
import { Icon } from './Icon';

/**
 * Sync notices. A transient status (the open doc changed on another device)
 * auto-dismisses; a conflict (a remote overwrite of a locally-diverged doc)
 * stays until the user reviews the preserved previous version or dismisses it —
 * divergence is never resolved silently.
 */
export function SyncToast() {
  const t = useT();
  const controller = useDocControllerContext();
  const notice = useStore((s) => s.syncNotice);
  const setNotice = useStore((s) => s.setSyncNotice);
  const conflict = useStore((s) => s.syncConflict);
  const vaultLocked = useStore((s) => s.vaultLocked);
  const setConflict = useStore((s) => s.setSyncConflict);

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(id);
  }, [notice, setNotice]);

  // Locked comes first: nothing else about sync is true until it is resolved.
  if (vaultLocked) {
    return (
      <div className="mb-sync-toast mb-sync-conflict" role="alert">
        <span>{t('vault.lockedToast')}</span>
        <button className="mb-btn" onClick={() => useStore.getState().setSettingsOpen(true)}>
          {t('vault.unlockGo')}
        </button>
      </div>
    );
  }

  if (conflict) {
    return (
      <div className="mb-sync-toast mb-sync-conflict" role="alert">
        <span>{t('sync.conflict')}</span>
        <button
          className="mb-btn"
          onClick={() => {
            const { docId } = conflict;
            setConflict(null);
            void controller.open(docId).then(() => useStore.getState().setHistoryOpen(true));
          }}
        >
          {t('sync.review')}
        </button>
        <button
          className="mb-sync-toast-x"
          onClick={() => setConflict(null)}
          aria-label={t('common.close')}
        >
          <Icon name="x" size={13} />
        </button>
      </div>
    );
  }

  if (!notice) return null;

  return (
    <div className="mb-sync-toast" role="status">
      {notice}
      <button
        className="mb-sync-toast-x"
        onClick={() => setNotice(null)}
        aria-label={t('common.close')}
      >
        <Icon name="x" size={13} />
      </button>
    </div>
  );
}
